import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, get, errMsg } from "@/lib/api";
import { toReview } from "@/lib/intake";
import { DropZone } from "@/components/intake/DropZone";
import { ParseProgress } from "@/components/intake/ParseProgress";
import { ReviewForm } from "@/components/intake/ReviewForm";
import { DuplicateCard } from "@/components/intake/DuplicateCard";
import { JobSelect } from "@/components/intake/JobSelect";

export function SingleUploadFlow({ jobId, onDone }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [imp, setImp] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(null);
  const [job, setJob] = useState(jobId || "");
  const [dup, setDup] = useState(null);
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({ queryKey: ["import", imp], queryFn: () => get(`/intake/imports/${imp}`), enabled: !!imp,
    refetchInterval: (q) => (["new", "duplicate", "failed"].includes(q.state.data?.rows?.[0]?.status) ? false : 1000) });
  const row = data?.rows?.[0];
  useEffect(() => { if (row && ["new", "duplicate"].includes(row.status) && !form) setForm(toReview(row.data)); }, [row, form]);

  const upload = async ([f]) => {
    if (!/\.(pdf|docx)$/i.test(f.name)) return toast.error("Upload a PDF or DOCX file.");
    if (f.size > 10 * 1024 * 1024) return toast.error("Files must be 10 MB or smaller.");
    setFile(f); setUploading(true);
    const fd = new FormData(); fd.append("file", f); if (job) fd.append("job_id", job);
    try { setImp((await api.post("/intake/single", fd)).data.import_id); } catch (e) { toast.error(errMsg(e)); setFile(null); } finally { setUploading(false); }
  };
  const reset = () => { setImp(null); setFile(null); setForm(null); setDup(null); };
  const save = async (action) => {
    if (!job) return toast.error("Pick a job for this candidate.");
    if (!form.name.trim()) return toast.error("Enter the candidate's name.");
    setSaving(true);
    try {
      const r = (await api.post(`/intake/single/${imp}/save`, { job_id: job, data: form, action, merge_into: dup?.id })).data;
      qc.invalidateQueries({ queryKey: ["candidates"] }); qc.invalidateQueries({ queryKey: ["ranked"] });
      toast.success(r.action === "merged" ? "Merged into the existing candidate" : `${form.name} added at Applied`, {
        description: "Screening and Fit Score are queued.", action: { label: "View candidate", onClick: () => nav(`/app/candidates/${r.candidate_id}/overview`) } });
      onDone?.(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.duplicate) setDup(d.duplicate); else toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  if (!imp) return (
    <div className="space-y-4">
      <DropZone accept=".pdf,.docx" onFiles={upload} disabled={uploading} testId="single-drop" title={uploading ? `Uploading ${file?.name}…` : "Drop a resume here or click to choose"} hint="PDF or DOCX, up to 10 MB" />
      {uploading && <p className="flex items-center gap-2 text-sm text-ink-secondary"><Loader2 size={14} className="animate-spin" aria-hidden="true" /> Checking the file…</p>}
    </div>
  );
  if (!form) return <ParseProgress filename={file?.name} progress={data?.parse_progress?.progress} message={data?.parse_progress?.message}
    failed={row?.status === "failed"} reason={row?.reason} onRetry={reset} />;
  return (
    <div className="space-y-6" data-testid="single-review-step">
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-ink-secondary">Job *</label>
        <JobSelect value={job} onChange={setJob} required testId="single-job-select" />
      </div>
      {row.low_fields?.length > 0 && <p className="rounded-[6px] bg-caution-tint/60 px-3 py-2 text-xs text-caution" data-testid="single-low-note">Fields in amber were hard to read. Please check them before saving.</p>}
      <ReviewForm value={form} onChange={(v) => { setForm(v); if (dup && (v.email !== form.email || v.phone !== form.phone)) setDup(null); }} low={row.low_fields || []} />
      {dup ? (
        <DuplicateCard dup={dup} testId="single-duplicate-card">
          <Button onClick={() => save("merge")} disabled={saving} data-testid="single-merge-btn">Merge into existing</Button>
          <Button variant="outline" onClick={() => save("create")} disabled={saving} data-testid="single-create-anyway-btn">Create anyway</Button>
        </DuplicateCard>
      ) : (
        <div className="sticky bottom-0 -mx-6 flex justify-end gap-2 border-t border-line bg-white/95 px-6 py-3">
          <Button variant="ghost" onClick={reset} data-testid="single-start-over">Use another file</Button>
          <Button onClick={() => save(null)} disabled={saving} data-testid="single-save-btn">{saving && <Loader2 size={14} className="animate-spin" aria-hidden="true" />} Save candidate</Button>
        </div>
      )}
    </div>
  );
}
